import type { MonospaceBlock, MonospacePage } from '#layers/data-model/public';
import type { WorkspaceEntityStore } from '#layers/runtime/public';
import { formatTitle } from '@directus/format-title';
import { getGeneratedKey, stripGeneratedKey } from '#layers/base/public';
import { blockExpressionMarker } from '#layers/block-engine/public';
import { isCollectionHidden } from '#layers/data-model/public';
import { CONTENT_ROUTE_NAME } from '#layers/spaces/constants/workspace-routes';
import { blockPropsFactory } from './block-props-factory';
import { spacePageFactory } from './space-page-factory';

export type GeneratedContentPage = MonospacePage & {
	block: MonospaceBlock | undefined;
};

type ContentPageDisplayOverride = Pick<MonospacePage, 'title' | 'icon' | 'iconColor'>;

interface GeneratedContentPageLookup {
	collectionId: NonNullable<MonospacePage['collectionId']>;
	type: NonNullable<MonospacePage['type']>;
	intent?: MonospacePage['intent'];
}

export function getGeneratedContentPages(store: WorkspaceEntityStore): GeneratedContentPage[] {
	const collections = store.MonospaceCollection.peekMany()
		.filter((collection) => !collection.isSystem)
		.toSorted((a, b) => a.apiName.localeCompare(b.apiName));

	return collections.flatMap((collection, index) => {
		// Ids used for customization with `override_page` column on MonospacePage
		// Do not change without considering backward compatibility
		const listingPageId = getGeneratedKey(`content:${collection.id}`);

		const meta = store.MonospaceCollectionMeta.peekFirst(collection.id);
		const collectionName = meta?.displayName ?? formatTitle(collection.apiName);
		const collectionIcon = meta?.icon ?? 'lucide:box';
		const collectionColor = meta?.color ?? null;
		const hidden = isCollectionHidden({ meta });

		const listingPage = spacePageFactory({
			id: listingPageId,
			title: collectionName,
			icon: collectionIcon,
			iconColor: collectionColor,
			spaceId: CONTENT_ROUTE_NAME,
			route: collection.apiName,
			sort: index,
			type: 'listing',
			collectionId: collection.id,
			isFullWidth: true,
			isHidden: hidden,
			block: {
				children: {
					listing: {
						blockId: '$builtin:Listing',
						props: blockPropsFactory({
							description: meta?.description ?? undefined,
							detailPageOnOpen: true,
							showAddItem: true,
							detailPageOnAdd: true,
							emptyState: {
								icon: 'lucide:send',
								description: '$t:layout.empty.description',
								actionLabel: '$t:common.add_item',
							},
						}),
						slots: {
							actions: [
								'create',
							],
						},
					},
				},
				rootChildren: [
					'listing',
				],
			},
		});

		const editDetailsPage = spacePageFactory({
			id: getGeneratedKey(`content:${collection.id}:detail:edit`),
			title: collectionName,
			icon: collectionIcon,
			iconColor: collectionColor,
			spaceId: CONTENT_ROUTE_NAME,
			route: ':itemId',
			sort: index,
			isHidden: true,
			type: 'detail',
			intent: 'edit',
			collectionId: collection.id,
			parentPageId: listingPageId,
			block: createContentDetailBlockRenderData(),
		});

		return [
			listingPage,
			editDetailsPage,
		];
	});
}

export function getGeneratedContentPage(
	store: WorkspaceEntityStore,
	lookup: GeneratedContentPageLookup,
): GeneratedContentPage | undefined {
	return findGeneratedContentPage(getGeneratedContentPages(store), lookup);
}

export function findGeneratedContentPage(pages: GeneratedContentPage[], {
	collectionId,
	type,
	intent = null,
}: GeneratedContentPageLookup): GeneratedContentPage | undefined {
	return pages.find((page) =>
		page.collectionId === collectionId
		&& page.type === type
		&& page.intent === intent,
	);
}

export function getContentPageOverrideId(page: Pick<MonospacePage, 'id'>): string {
	return stripGeneratedKey(page.id);
}

/**
 * The row that replaces `page` once the user customizes it, minus its id.
 *
 * Kept next to the generator so "what a customized copy is" cannot drift from "what a generated
 * page is". `overridePage` is unique in the database and is what links the two.
 */
export function createContentPageOverrideInput(page: GeneratedContentPage) {
	return {
		title: page.title,
		route: `${page.route}-custom`,
		icon: page.icon,
		iconColor: page.iconColor,
		isFullWidth: page.isFullWidth,
		spaceId: null,
		sectionId: page.sectionId,
		type: page.type,
		collectionId: page.collectionId,
		intent: page.intent,
		overridePage: getContentPageOverrideId(page),
		sort: page.sort,
		isHidden: page.isHidden,
		description: page.description,
	};
}

/**
 * The row that replaces `page`, if the user has customized it.
 *
 * Takes the candidates rather than reading the cache: a cache-only lookup cannot tell "not
 * customized" apart from "garbage-collected", which is what made the create path duplicate rows.
 * `useWorkspacePagesStore` owns the list this reads from.
 */
export function findContentPageOverride(
	overridePages: MonospacePage[],
	page: Pick<MonospacePage, 'id'>,
): MonospacePage | undefined {
	const overridePageId = getContentPageOverrideId(page);

	return overridePages.find((candidate) => candidate.overridePage === overridePageId);
}

export function applyContentPageDisplayOverride(
	page: GeneratedContentPage,
	overridePage: ContentPageDisplayOverride | undefined,
): GeneratedContentPage {
	if (!overridePage) {
		return page;
	}

	return {
		...page,
		title: overridePage.title,
		icon: overridePage.icon,
		iconColor: overridePage.iconColor,
	};
}

export function createContentDetailBlockRenderData() {
	return {
		children: {
			form: {
				blockId: '$builtin:Form',
				props: blockPropsFactory({
					itemId: `${blockExpressionMarker}{{$route.params.itemId}}`,
				}),
			},
		},
		rootChildren: [
			'form',
		],
	};
}
